import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { kUserFilesApiBasePath } from '@/constants/constants-api-paths'
import { kUserFilesGetMaxFileIds } from '@/user_files/limits'
import { ApiError } from '@/util/errors/api-error'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { chunkArray, uniquePreserveOrder } from '@/util/array-utils'
import { formatUserFilesErrorDetail } from '@/user_files/utils/api-error'
import { FileDataBatchResponse, FileDataBatchResponseSchema } from '../schemas'

/**
 * Fetch file data for specified file IDs.
 * @param fileIds - UUIDs of files to fetch
 * @param triggerStatusRefresh - When true, triggers backend refresh for files in processing states
 * @returns FileDataBatchResponse containing files keyed by server ID
 * @throws Error when user is not authenticated
 * @throws ApiError when the user-files batch request fails
 */
export async function getUserFiles(fileIds: string[], triggerStatusRefresh = false): Promise<FileDataBatchResponse> {
  const sentryTransactionId = nanoid()
  const uniqueFileIds = uniquePreserveOrder(fileIds)

  if (uniqueFileIds.length === 0) {
    return { files: {} }
  }

  try {
    const auth = getAuth()
    const token = await auth.currentUser?.getIdToken()

    if (!token) {
      throw new Error('User not authenticated')
    }

    const fileIdChunks = chunkArray(uniqueFileIds, kUserFilesGetMaxFileIds)
    const merged: FileDataBatchResponse['files'] = {}

    for (const chunk of fileIdChunks) {
      const params = new URLSearchParams()
      chunk.forEach((id) => params.append('file_ids', id))
      if (triggerStatusRefresh) {
        params.append('triggerStatusRefresh', 'true')
      }

      const url = `${kPaxtonAppApiRESTBaseUrl()}${kUserFilesApiBasePath}/batch?${params.toString()}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'X-Transaction-Id': sentryTransactionId,
        },
      })

      const { data, parseError } = await readResponseJson(response)
      if (!response.ok) {
        const errorData = parseError ? {} : data
        const detail = formatUserFilesErrorDetail(errorData)

        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error(`Fetch error - status: ${response.status}, detail: ${detail}`), {
            extra: {
              ...getResponseDebugContext(response, url),
              triggerStatusRefresh,
              requestedFileCount: uniqueFileIds.length,
              chunkSize: chunk.length,
              responseData: errorData,
              parseError: parseError ? formatUnknownError(parseError) : null,
            },
          })
        })

        throw new ApiError(response.status, detail || `Failed to fetch user files: ${response.status}`)
      }

      if (parseError) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error('User files response JSON parse failed'), {
            extra: {
              ...getResponseDebugContext(response, url),
              triggerStatusRefresh,
              requestedFileCount: uniqueFileIds.length,
              chunkSize: chunk.length,
              parseError: formatUnknownError(parseError),
            },
          })
        })

        throw new ApiResponsePayloadError(response.status, 'Invalid user files response JSON')
      }

      const parsed = FileDataBatchResponseSchema.safeParse(data)
      if (!parsed.success) {
        Sentry.withScope((scope) => {
          scope.setTags({ transaction_id: sentryTransactionId })
          Sentry.captureException(new Error('User files response schema validation failed'), {
            extra: {
              ...getResponseDebugContext(response, url),
              triggerStatusRefresh,
              requestedFileCount: uniqueFileIds.length,
              chunkSize: chunk.length,
              responseKeys: getTopLevelKeys(data),
              issues: parsed.error.issues,
            },
          })
        })

        throw new ApiResponsePayloadError(response.status, 'Invalid user files response shape', parsed.error.issues)
      }

      Object.assign(merged, parsed.data.files)
    }

    return { files: merged }
  } catch (error) {
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: {
            triggerStatusRefresh,
            requestedFileCount: uniqueFileIds.length,
          },
        })
      })
    }
    throw error
  }
}
